import React from 'react';
import { observer } from 'mobx-react-lite';
import { Badge, Box, Flex, Heading, ScrollArea, Table, Text, TextField } from '@radix-ui/themes';
import { useSearchParams } from 'react-router-dom';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';

import APNavBar from 'Components/adminpanel/Elements/APNavBar';
import { AdminSideBar } from 'admin/elements/side-bar';
import { Paginator } from 'shared/components/pagination';
import { Lead, LeadSortKey, LeadStatus } from './types';
import { LeadsBrowserFilters } from './leads-browser-filters';
import { useLeadsBrowser } from './leads-browser.provider';
import ReactJsonView from '@microlink/react-json-view';
import type { InteractionProps } from '@microlink/react-json-view';

import './leads-browser.scss';

const SKELETON_ROWS = 12;
const STATUS_COLOR: Record<LeadStatus, 'amber' | 'green' | 'red'> = {
   pending: 'amber',
   processed: 'green',
   failed: 'red',
};

const renderValue = (value: string | number | null | undefined) => value ?? '-';

const sortLabel = (active: boolean, direction: 'asc' | 'desc') => {
   if (!active) {
      return '';
   }

   return direction === 'asc' ? ' ^' : ' v';
};

const formatDateTime = (value: string | null) => {
   if (!value) {
      return '-';
   }

   const date = new Date(value);
   if (Number.isNaN(date.getTime())) {
      return value;
   }

   return new Intl.DateTimeFormat('uk-UA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
   }).format(date);
};

const canEditLeadValue = (edit: InteractionProps) =>
   edit.namespace[edit.namespace.length - 1] === 'values' && edit.name !== 'values';

const renderLeadData = (data: Lead['data'], onDataChange: (data: Lead['data']) => void) => {
   if (!data) {
      return '-';
   }

   return (
         <ReactJsonView
            src={data}
            theme="flat"
            name={false}
            onAdd={false}
            onDelete={false}
            onEdit={(edit) => {
               if (!canEditLeadValue(edit)) {
                  return false;
               }

               onDataChange(edit.updated_src as Lead['data']);
               return true;
            }}
            validationMessage="Only values can be edited"
            displayDataTypes={false}
            enableClipboard={false}
            style={{
               minWidth: '100%',
               fontSize: '16px',
               lineHeight: '1.65',
            }}
         />
   );
};

function LeadsBrowserView() {
   const leadsStore = useLeadsBrowser();
   const [searchParams, setSearchParams] = useSearchParams();
   const initFromQueryDone = React.useRef(false);
   const [expandedLeadId, setExpandedLeadId] = React.useState<number | null>(null);
   const skeletonRows = Array.from({ length: SKELETON_ROWS }, (_, index) => index);

   React.useEffect(() => {
      if (initFromQueryDone.current) {
         return;
      }
      initFromQueryDone.current = true;

      const rawPage = searchParams.get('page');
      const parsedPage = Number(rawPage);
      const page = Number.isFinite(parsedPage) && parsedPage >= 1 ? parsedPage : 1;
      void leadsStore.loadPage(page - 1);
   }, [leadsStore, searchParams]);

   React.useEffect(() => {
      const nextPage = String(leadsStore.pagination.page + 1);
      if (searchParams.get('page') === nextPage) {
         return;
      }

      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set('page', nextPage);
      setSearchParams(nextSearchParams, { replace: true });
   }, [leadsStore.pagination.page, searchParams, setSearchParams]);

   const renderSortHeader = (label: string, key: LeadSortKey, className?: string) => (
      <Table.ColumnHeaderCell className={className}>
         <button
            className="leads-browser__sort-button"
            type="button"
            onClick={() => {
               void leadsStore.onSortBy(key);
            }}
         >
            {label}
            {sortLabel(leadsStore.sorter === key, leadsStore.direction)}
         </button>
      </Table.ColumnHeaderCell>
   );

   return (
      <>
         <APNavBar />
         <Box pt="80px" className="leads-browser">
            <Flex width="270px" minWidth="270px">
               <AdminSideBar />
            </Flex>

            <Box className="leads-browser__content">
               <Flex py="1" mx="3" justify="between" align="center" gap="3">
                  <Heading as="h1" weight="medium">
                     Leads Browser
                  </Heading>

                  <Flex align="center" gap="2">
                     <Text size="3" color="gray">
                        Records:
                     </Text>
                     <Text size="3" weight="medium">
                        {leadsStore.recordStart} - {leadsStore.recordEnd} of {leadsStore.totalCount}
                     </Text>
                  </Flex>
               </Flex>

               <LeadsBrowserFilters />

               <ScrollArea scrollbars="both" type="always" className="leads-browser__scroll-area">
                  <Box p="3" className="leads-browser__table-wrap">
                     <Table.Root
                        size="3"
                        layout="fixed"
                        variant="surface"
                        className="leads-browser__table"
                     >
                        <Table.Header>
                           <Table.Row>
                              {renderSortHeader('ID', 'id', 'leads-browser__id-cell')}
                              {renderSortHeader(
                                 'Facebook Lead ID',
                                 'fb_lead_id',
                                 'leads-browser__fb-id-cell'
                              )}
                              {renderSortHeader(
                                 'Page ID',
                                 'page_id',
                                 'leads-browser__page-id-cell'
                              )}
                              {renderSortHeader('Status', 'status', 'leads-browser__status-cell')}
                              <Table.ColumnHeaderCell className="leads-browser__data-action-cell">
                                 Data
                              </Table.ColumnHeaderCell>
                              <Table.ColumnHeaderCell className="leads-browser__log-cell">
                                 Log
                              </Table.ColumnHeaderCell>
                              {renderSortHeader(
                                 'Created',
                                 'created_at',
                                 'leads-browser__date-cell'
                              )}
                              {renderSortHeader(
                                 'Updated',
                                 'updated_at',
                                 'leads-browser__date-cell'
                              )}
                           </Table.Row>
                        </Table.Header>

                        <Table.Body>
                           {leadsStore.loading &&
                              skeletonRows.map((row) => (
                                 <Table.Row key={`skeleton-${row}`} className="lead-skeleton-row">
                                    {Array.from({ length: 8 }, (_, cell) => (
                                       <Table.Cell key={`skeleton-${row}-${cell}`}>
                                          <div className="lead-skeleton" />
                                       </Table.Cell>
                                    ))}
                                 </Table.Row>
                              ))}

                           {!leadsStore.loading && leadsStore.leads.length === 0 && (
                              <Table.Row>
                                 <Table.Cell colSpan={8} align="center">
                                    No records found
                                 </Table.Cell>
                              </Table.Row>
                           )}

                           {!leadsStore.loading &&
                              leadsStore.leads.map((lead) => {
                                 const isExpanded = expandedLeadId === lead.id;

                                 return (
                                    <React.Fragment key={lead.id}>
                                       <Table.Row>
                                          <Table.Cell className="leads-browser__id-cell">
                                             {renderValue(lead.id)}
                                          </Table.Cell>
                                          <Table.Cell className="leads-browser__fb-id-cell">
                                             {renderValue(lead.fb_lead_id)}
                                          </Table.Cell>
                                          <Table.Cell className="leads-browser__page-id-cell">
                                             {renderValue(lead.page_id)}
                                          </Table.Cell>
                                          <Table.Cell className="leads-browser__status-cell">
                                             <Badge
                                                color={STATUS_COLOR[lead.status] ?? 'gray'}
                                                variant="soft"
                                             >
                                                {lead.status}
                                             </Badge>
                                          </Table.Cell>
                                          <Table.Cell className="leads-browser__data-action-cell">
                                             <button
                                                type="button"
                                                className="leads-browser__data-toggle"
                                                aria-expanded={isExpanded}
                                                onClick={() => {
                                                   setExpandedLeadId(isExpanded ? null : lead.id);
                                                }}
                                             >
                                                {isExpanded ? 'Hide data' : 'Show data'}
                                                {isExpanded ? (
                                                   <KeyboardArrowUpOutlinedIcon fontSize="small" />
                                                ) : (
                                                   <KeyboardArrowDownOutlinedIcon fontSize="small" />
                                                )}
                                             </button>
                                          </Table.Cell>
                                          <Table.Cell
                                             className="leads-browser__log-cell"
                                             title={lead.log ?? undefined}
                                          >
                                             {renderValue(lead.log)}
                                          </Table.Cell>
                                          <Table.Cell className="leads-browser__date-cell">
                                             {formatDateTime(lead.created_at)}
                                          </Table.Cell>
                                          <Table.Cell className="leads-browser__date-cell">
                                             {formatDateTime(lead.updated_at)}
                                          </Table.Cell>
                                       </Table.Row>

                                       {isExpanded && (
                                          <Table.Row className="leads-browser__expanded-row">
                                             <Table.Cell colSpan={8}>
                                                <div className="leads-browser__expanded-json">
                                                   {renderLeadData(lead.data, (data) => {
                                                      leadsStore.updateLeadData(lead.id, data);
                                                   })}
                                                </div>
                                             </Table.Cell>
                                          </Table.Row>
                                       )}
                                    </React.Fragment>
                                 );
                              })}
                        </Table.Body>
                     </Table.Root>
                  </Box>
               </ScrollArea>

               <Flex
                  className="leads-browser__pagination"
                  py="2"
                  px="3"
                  justify="between"
                  align="center"
                  gap="3"
               >
                  <Box />

                  <Paginator
                     totalPages={leadsStore.totalPages}
                     currentPage={leadsStore.pagination.page}
                     disabled={leadsStore.loading}
                     onPageClick={(page) => {
                        void leadsStore.loadPage(page);
                     }}
                     setRange={(range) => {
                        leadsStore.pagination.setRange(range);
                     }}
                     range={leadsStore.pagination.range}
                  />

                  <Flex align="center" gap="2">
                     <Flex align="center" gap="1">
                        <Text size="3" color="gray">
                           Total pages:
                        </Text>
                        <Text size="3" weight="medium">
                           {leadsStore.totalPages === 0 ? '' : leadsStore.totalPages}
                        </Text>
                     </Flex>

                     <TextField.Root
                        className="leads-browser__page-input"
                        value={leadsStore.toPage}
                        disabled={leadsStore.loading}
                        onChange={(e) => {
                           leadsStore.onToPageChange(e.target.value);
                        }}
                        onKeyDown={(e) => {
                           if (e.key === 'Enter') {
                              void leadsStore.goToPage();
                           }
                        }}
                        size="3"
                        color="blue"
                        type="number"
                        min={1}
                        max={leadsStore.totalPages}
                     />
                  </Flex>
               </Flex>
            </Box>
         </Box>
      </>
   );
}

export const LeadsBrowser = observer(LeadsBrowserView);
